/**
 * Variant Cards Custom
 * Gère les interactions des cartes de variantes
 * Basé sur la maquette React
 */

class VariantCardsCustom extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.init();
  }

  init() {
    // Charger les données JSON des variantes
    this.loadVariantsData();

    // Setup des cartes
    this.cards = this.querySelectorAll('.product-state-card');
    this.setupQuantityButtons();
    this.setupAddToCartButtons();
  }

  /**
   * Charge les données des variantes depuis le script JSON
   */
  loadVariantsData() {
    const jsonScript = this.querySelector('[data-variant-json]');
    if (jsonScript) {
      try {
        this.variants = JSON.parse(jsonScript.textContent);
      } catch (error) {
        console.error('Erreur de parsing JSON des variantes:', error);
      }
    }
  }

  /**
   * Configure les boutons +/- de quantité
   */
  setupQuantityButtons() {
    this.cards.forEach((card) => {
      const decreaseBtn = card.querySelector('[data-quantity-decrease]');
      const increaseBtn = card.querySelector('[data-quantity-increase]');
      const input = card.querySelector('[data-quantity-input]');

      if (!input) return;

      // Bouton diminuer
      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const minValue = parseInt(input.min) || 1;

          if (currentValue > minValue) {
            input.value = currentValue - 1;
            this.updateButtonStates(card, input);
          }
        });
      }

      // Bouton augmenter
      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const maxValue = parseInt(input.max) || 999;

          if (currentValue < maxValue) {
            input.value = currentValue + 1;
            this.updateButtonStates(card, input);
          }
        });
      }

      // Validation de l'input
      input.addEventListener('input', () => {
        this.validateQuantityInput(input);
        this.updateButtonStates(card, input);
      });

      // État initial des boutons
      this.updateButtonStates(card, input);
    });
  }

  /**
   * Valide l'input de quantité
   */
  validateQuantityInput(input) {
    const value = parseInt(input.value);
    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || 999;

    if (isNaN(value) || value < min) {
      input.value = min;
    } else if (value > max) {
      input.value = max;
    }
  }

  /**
   * Met à jour l'état des boutons +/- selon la quantité
   */
  updateButtonStates(card, input) {
    const decreaseBtn = card.querySelector('[data-quantity-decrease]');
    const increaseBtn = card.querySelector('[data-quantity-increase]');
    const currentValue = parseInt(input.value) || 1;
    const minValue = parseInt(input.min) || 1;
    const maxValue = parseInt(input.max) || 999;

    // Désactiver le bouton - si à la quantité min
    if (decreaseBtn) {
      decreaseBtn.disabled = currentValue <= minValue;
      decreaseBtn.classList.toggle('is-disabled', currentValue <= minValue);
    }

    // Désactiver le bouton + si à la quantité max
    if (increaseBtn) {
      increaseBtn.disabled = currentValue >= maxValue;
      increaseBtn.classList.toggle('is-disabled', currentValue >= maxValue);
    }
  }

  /**
   * Configure les boutons "Ajouter au devis"
   */
  setupAddToCartButtons() {
    this.cards.forEach((card) => {
      const form = card.querySelector('.product-state-card__form');
      const addButton = card.querySelector('[data-add-to-cart]');

      if (!form || !addButton) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        // Désactiver le bouton pendant l'ajout
        addButton.disabled = true;
        const originalText = addButton.textContent;
        addButton.textContent = 'Ajout en cours...';

        try {
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            const data = await response.json();

            // Feedback visuel de succès
            addButton.textContent = '✓ Ajouté !';
            addButton.classList.add('is-success');

            // Déclencher un événement custom pour mettre à jour le panier
            this.dispatchEvent(new CustomEvent('variant:added-to-cart', {
              bubbles: true,
              detail: { variant: data }
            }));

            // Restaurer le bouton après 2s
            setTimeout(() => {
              addButton.textContent = originalText;
              addButton.classList.remove('is-success');
              addButton.disabled = false;
            }, 2000);

          } else {
            throw new Error('Erreur lors de l\'ajout au panier');
          }

        } catch (error) {
          console.error('Erreur d\'ajout au panier:', error);

          // Feedback d'erreur
          addButton.textContent = 'Erreur - Réessayer';
          addButton.classList.add('is-error');

          // Restaurer le bouton après 2s
          setTimeout(() => {
            addButton.textContent = originalText;
            addButton.classList.remove('is-error');
            addButton.disabled = false;
          }, 2000);
        }
      });
    });
  }
}

// Enregistrer le custom element
if ('customElements' in window) {
  customElements.define('variant-cards-custom', VariantCardsCustom);
}

/**
 * Fallback pour les navigateurs sans support des custom elements
 */
if (!('customElements' in window)) {
  document.addEventListener('DOMContentLoaded', () => {
    const containers = document.querySelectorAll('[id^="variant-cards-"]');
    containers.forEach((container) => {
      const instance = Object.create(VariantCardsCustom.prototype);
      VariantCardsCustom.call(instance);

      // Copier les propriétés
      Object.assign(container, instance);
      instance.init.call(container);
    });
  });
}
